import { useCallback, useMemo } from 'react';

// eslint-disable-next-line no-restricted-imports
import { isFedrampModerate, isIsolatedCloud } from '@atlaskit/atlassian-context';
import type { EditorActions } from '@atlaskit/editor-core';
import {
	getConfluencePageAri,
	useMemoizedBlockServiceAPIProviders,
	useMemoizedSyncedBlockProvider,
} from '@atlaskit/editor-synced-block-provider';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	useMatchRoute,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { createSmartCardEventHandler } from '@confluence/linking-platform/entry-points/createSmartCardEventHandler.ts';
import { createRelaySyncBlockFetchProvider } from '@confluence/fabric-providers/entry-points/relaySyncBlockFetchProvider.ts';
import { getTaskDecisionProvider } from '@confluence/fabric-providers/entry-points/TaskDecisionProvider.ts';
import { useProfilecardProvider } from '@confluence/fabric-providers/entry-points/useProfileCardProvider.ts';
import { useMemoizedSyncedBlockNestedRendererProviderCreator } from '@confluence/fabric-providers/entry-points/SyncedBlockProvider.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { syncedBlockLoadSSRData } from '@confluence/fabric-providers/entry-points/syncedBlockLoadSSRData.ts';

import { useMentionProvider } from '../useMentionProvider.ts';

export type SyncedBlockProviderForEditorProps = {
	contentId: string | undefined;
	contentType: string | undefined;
	focusedMentionId?: string;
	spaceId: string | null | undefined;
	editorActions: EditorActions;
};
export const useMemoizedSyncedBlockProviderForEditorEnabled = ({
	contentId: confluencePageId,
	contentType,
	focusedMentionId,
	spaceId,
	editorActions,
}: SyncedBlockProviderForEditorProps) => {
	const { cloudId, userId, featureFlags } = useSessionData();
	const { push } = useRouteActions();

	const confluencePageAri = useMemo(
		() =>
			getConfluencePageAri({
				pageId: confluencePageId ?? '',
				cloudId,
				pageType: contentType === 'page' ? 'page' : 'blogpost',
			}),
		[cloudId, confluencePageId, contentType],
	);

	const [{ collabEditProvider: extractedCollabEditProvider }] = useNativeCollabState();

	const getCollabVersion = useCallback(async () => {
		try {
			const ackState = await editorActions.getResolvedEditorState('publish');
			return ackState?.stepVersion;
		} catch {
			return extractedCollabEditProvider?.getCurrentPmVersion();
		}
	}, [extractedCollabEditProvider, editorActions]);

	const { fetchProvider: blockServiceFetchProvider, writeProvider: blockServiceWriteProvider } =
		useMemoizedBlockServiceAPIProviders({
			parentAri: confluencePageAri ?? '',
			parentId: confluencePageId ?? '',
			product: 'confluence-page',
			cloudId,
			getVersion: getCollabVersion,
		});

	const syncedBlockNestedRendererProviderCreator =
		useMemoizedSyncedBlockNestedRendererProviderCreator(cloudId, userId);

	const mentionProvider = useMentionProvider(spaceId, undefined, focusedMentionId);
	const profilecardProvider = useProfilecardProvider({
		push,
		cloudId,
		featureFlags,
		userId,
	});
	const taskDecisionProvider = getTaskDecisionProvider();

	const providerOptions = useMemo(() => {
		return {
			parentDataProviders: {
				mentionProvider,
				profilecardProvider,
				taskDecisionProvider,
			},
			providerCreator: syncedBlockNestedRendererProviderCreator,
		};
	}, [
		mentionProvider,
		profilecardProvider,
		taskDecisionProvider,
		syncedBlockNestedRendererProviderCreator,
	]);

	// Wrap the fetch provider with Relay subscription support
	const relayFetchProvider = useMemo(
		() => createRelaySyncBlockFetchProvider(blockServiceFetchProvider, cloudId),
		[blockServiceFetchProvider, cloudId],
	);

	const syncedBlockProvider = useMemoizedSyncedBlockProvider({
		fetchProvider: relayFetchProvider,
		writeProvider: blockServiceWriteProvider,
		providerOptions,
		getSSRData: syncedBlockLoadSSRData,
	});

	return confluencePageId ? syncedBlockProvider : undefined;
};

export const useMemoizedSyncedBlockProviderForEditorDisabled = (
	_props: SyncedBlockProviderForEditorProps,
): undefined => {
	return undefined;
};

const isFedrampOrIsolatedCloud = (): boolean => {
	return isFedrampModerate() || isIsolatedCloud();
};

export const useMemoizedSyncedBlockProviderForEditor = conditionalHooksFactory(
	() => !isFedrampOrIsolatedCloud() && isExperimentEnabled('platform_synced_block'),
	useMemoizedSyncedBlockProviderForEditorEnabled,
	useMemoizedSyncedBlockProviderForEditorDisabled,
);

type SyncedBlockForEmbeddedEditorProps = {
	contentId: string | undefined;
	contentType: string | undefined;
	spaceId: string | null | undefined;
	editorActions: EditorActions;
};

const useSyncedBlockForEmbeddedEditorEnabled = ({
	contentId,
	contentType,
	spaceId,
	editorActions,
}: SyncedBlockForEmbeddedEditorProps) => {
	const syncedBlockProvider = useMemoizedSyncedBlockProviderForEditorEnabled({
		contentId,
		contentType,
		spaceId,
		editorActions,
	});
	const matchRoute = useMatchRoute();
	const { push } = useRouteActions();
	const syncedBlockEventHandlers = useMemo(
		() => createSmartCardEventHandler({ matchRoute, push }, true),
		[matchRoute, push],
	);
	return { syncedBlockProvider, syncedBlockEventHandlers };
};

const useSyncedBlockForEmbeddedEditorDisabled = () => ({
	syncedBlockProvider: undefined,
	syncedBlockEventHandlers: undefined,
});

export const useSyncedBlockForEmbeddedEditor = conditionalHooksFactory(
	() => isExperimentEnabled('platform_synced_block'),
	useSyncedBlockForEmbeddedEditorEnabled,
	useSyncedBlockForEmbeddedEditorDisabled,
);
